<template>
  <div
    class="relative min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4 sm:p-6 lg:p-8"
  >
    <!-- Theme Toggle -->
    <button
      type="button"
      class="fixed top-4 right-4 z-50 flex items-center justify-center w-10 h-10 rounded-full bg-white dark:bg-gray-800 shadow-lg border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:shadow-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary/50"
      @click="toggleTheme"
    >
      <component :is="isDarkMode ? Sun : Moon" class="h-5 w-5" />
    </button>

    <div class="w-full max-w-md">

      <!-- Login Card -->
      <div class="auth-card bg-white dark:bg-gray-700 rounded-3xl shadow-xl px-8 py-10 sm:px-10 sm:py-12">
        <!-- Logo -->
        <div class="flex justify-center mb-8">
          <img
            :src="logoImage"
            alt="Company Logo"
            class="h-32 w-auto"
            :style="isDarkMode ? 'filter: drop-shadow(0 0 .5px #fff) drop-shadow(0 0 .5px #fff) drop-shadow(0 0 .5px #fff) drop-shadow(0 0 .5px #fff);' : ''"
          />
        </div>

        <!-- Heading -->
        <div class="text-center mb-8">
          <h1 class="text-2xl font-bold text-gray-900 dark:text-white">
            Welcome!
          </h1>
          <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">
            Sign in to your account to continue
          </p>
        </div>

        <!-- Already logged in (inside iframe) -->
        <div
          v-if="isAlreadyLoggedIn"
          class="space-y-5"
        >
          <div class="rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-6 text-center">
            <div class="flex justify-center mb-4">
              <div class="h-14 w-14 rounded-full bg-blue-100 dark:bg-blue-800 flex items-center justify-center">
                <CheckCircle class="h-7 w-7 text-blue-600 dark:text-blue-300" />
              </div>
            </div>
            <h2 class="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              You're already signed in
            </h2>
            <p class="text-sm text-gray-600 dark:text-gray-300 mb-6">
              The portal is ready for you. Open it in a new tab to continue.
            </p>
            <button
              type="button"
              @click="openPortalInNewTab"
              class="btn btn-primary w-full py-2.5"
            >
              Open Portal
            </button>
            <button
              type="button"
              @click="handleLogout"
              class="mt-3 w-full text-sm text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
            >
              Sign out
            </button>
          </div>
        </div>

        <!-- Success Message -->
        <div
          v-if="successMessage && !isAlreadyLoggedIn"
          class="mb-6 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 p-4"
        >
          <div class="flex">
            <div class="flex-shrink-0">
              <CheckCircle class="h-5 w-5 text-emerald-500" />
            </div>
            <div class="ml-3">
              <p class="text-sm text-emerald-700 dark:text-emerald-300">{{ successMessage }}</p>
            </div>
          </div>
        </div>

        <!-- Login Form -->
        <form v-if="!isAlreadyLoggedIn" class="space-y-5" @submit.prevent="handleLogin(rememberMe)">
          <div>
            <label for="email-address" class="label">Email address</label>
            <div class="mt-1.5">
              <input
                id="email-address"
                name="email"
                type="email"
                autocomplete="email"
                required
                v-model="email"
                class="input"
                placeholder="you@example.com"
              />
            </div>
          </div>

          <div>
            <label for="password" class="label">Password</label>
            <div class="mt-1.5 relative">
              <input
                id="password"
                name="password"
                :type="showPassword ? 'text' : 'password'"
                autocomplete="current-password"
                required
                v-model="password"
                class="input pr-10"
                placeholder="••••••••"
              />
              <button
                type="button"
                @click="showPassword = !showPassword"
                class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
              >
                <EyeOff v-if="showPassword" class="h-5 w-5" />
                <Eye v-else class="h-5 w-5" />
              </button>
            </div>
          </div>

          <div class="flex items-center justify-between">
            <div class="flex items-center">
              <input
                id="remember-me"
                name="remember-me"
                type="checkbox"
                v-model="rememberMe"
                class="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
              />
              <label
                for="remember-me"
                class="ml-2 block text-[12px] md:text-[15px] text-gray-600 dark:text-gray-400"
              >
                Remember me
              </label>
            </div>
            <router-link
              to="/forgot-password"
              class="text-[12px] md:text-[15px] font-medium text-primary dark:text-primary-dark-text hover:underline"
            >
              Forgot password?
            </router-link>
          </div>

          <button type="submit" :disabled="loading" class="btn btn-primary w-full py-2.5">
            <span v-if="loading" class="flex items-center justify-center">
              <svg
                class="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Signing in...
            </span>
            <span v-else>Sign in</span>
          </button>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import logoImage from '@/assets/company-logo.png'
import { useTitle } from '@/composables/ui/useTitle'
import { useLogin } from '@/composables/auth/useLogin'
import { useAuthStore } from '@/stores/auth'
import { useThemeStore } from '@/stores/theme'
import { Sun, Moon, CheckCircle, Eye, EyeOff } from 'lucide-vue-next'

useTitle('Login')

const authStore = useAuthStore()
const { email, password, loading, handleLogin, handleLogout } = useLogin()
const successMessage = ref('')
const rememberMe = ref(false)
const showPassword = ref(false)

const themeStore = useThemeStore()
const isDarkMode = computed(() => themeStore.isDarkMode)
const toggleTheme = () => {
  themeStore.toggleTheme()
}

// Detect if running inside an iframe (e.g., embedded on WordPress)
const isInIframe = computed(() => window.self !== window.top)

// Show "already logged in" state when authenticated inside an iframe
const isAlreadyLoggedIn = computed(() => authStore.isAuthenticated && isInIframe.value)

function openPortalInNewTab() {
  const baseUrl = process.env.VUE_APP_BASE_URL || ''
  const targetPath = authStore.isAdmin ? '/employee' : '/dashboard'
  const portalUrl = window.location.origin + baseUrl + targetPath
  window.open(portalUrl, '_blank')
}
</script>


